import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import { useAuthContext } from "../Auth";
import { instance } from "../../api/apiBase";
import { useIsBoomScreenOnly } from "shared/app/BoomCrmPaywall/hooks";
import {
  CRM_ONLY_NOTIFICATION_TYPES,
  type Notification,
  type NotificationType
} from "views/boom-notifications/types";

const NotificationContext = createContext<{
  newestNotifications: Notification[] | undefined;
  totalUnread: number;
  refetchTotalUnreadNotification: () => void;
}>({
  newestNotifications: [],
  totalUnread: 0,
  refetchTotalUnreadNotification: () => {}
});

const notificationApi = {
  getNewest: () =>
    instance.get("/portal/v1/notifications/newest").then((res) => res.data),
  getTotalUnread: () =>
    instance
      .get("/portal/v1/notifications/unread_count")
      .then((res) => res.data?.total_unread || 0)
};

// One string per feed state: a new row or a row read elsewhere both change it.
const feedSignature = (notifications: Notification[]) =>
  notifications.map((n) => `${n.id}:${n.read_at ?? ""}`).join(",");

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { auth } = useAuthContext();
  const isBoomScreenOnly = useIsBoomScreenOnly();
  const queryClient = useQueryClient();

  const { data: totalUnread, refetch: refetchTotalUnreadNotification } =
    useQuery({
      queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"],
      queryFn: () => notificationApi.getTotalUnread(),
      enabled: Boolean(auth)
    });

  const { data: newestNotifications } = useQuery<Notification[]>({
    queryKey: ["NEWEST_NOTIFICATIONS"],
    queryFn: () => notificationApi.getNewest(),
    refetchInterval: 30000,
    enabled: Boolean(auth)
  });

  // This 30s poll is the only "something changed" signal the portal has for
  // notifications. The Notifications page keeps its own per-tab unread counts
  // and list (UNREAD_NOTIFICATIONS_COUNT / NOTIFICATIONS), fetched on mount
  // only, so without this bridge a tab badge stayed stale until the user
  // navigated away and back. Seed on the first load; after that any change in
  // the feed refreshes the page queries and the bell total together.
  const feedSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!newestNotifications) return;

    const signature = feedSignature(newestNotifications);
    const previous = feedSignatureRef.current;
    feedSignatureRef.current = signature;
    if (previous === null || previous === signature) return;

    queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
    queryClient.invalidateQueries({ queryKey: ["UNREAD_NOTIFICATIONS_COUNT"] });
    queryClient.invalidateQueries({ queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"] });
  }, [newestNotifications, queryClient]);

  // CRM-564: BoomScreen-only partners shouldn't see notifications tied to
  // gated Tasks/Leads surfaces. Filter the toast feed; for the total badge,
  // approximate from the filtered newest list (the unread-count endpoint
  // returns CRM-inclusive totals).
  const visibleNewestNotifications = useMemo(() => {
    if (!isBoomScreenOnly) return newestNotifications;
    return (newestNotifications || []).filter(
      (n) => !CRM_ONLY_NOTIFICATION_TYPES.has(n.type as NotificationType)
    );
  }, [isBoomScreenOnly, newestNotifications]);

  const effectiveTotalUnread = isBoomScreenOnly
    ? (visibleNewestNotifications || []).filter((n) => !n.read_at).length
    : totalUnread;

  return (
    <NotificationContext.Provider
      value={{
        newestNotifications: visibleNewestNotifications,
        totalUnread: effectiveTotalUnread,
        refetchTotalUnreadNotification
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationContext() {
  return useContext(NotificationContext);
}
